/**
 * useListboxNav — the keyboard half of an input-anchored suggestion list,
 * lifted from `Select`'s listbox implementation (sweep 2 Pass D, #243).
 *
 * Owns the highlighted index, ArrowUp/ArrowDown/Enter, keeping the highlight
 * in view, and the option/listbox ids that wire `aria-activedescendant`.
 * Home/End are deliberately NOT captured — unlike Select's button trigger,
 * these popups anchor to a real text input where Home/End move the caret.
 * Escape and outside-click dismissal are `useDismissable`'s job.
 */
import { useEffect, useId, useState, type KeyboardEvent } from "react";

export interface ListboxNavOptions {
  /** Whether the suggestion list is showing. */
  open: boolean;
  /** Number of options currently listed. */
  count: number;
  /** Commit the highlighted option (same effect as clicking its row). */
  onCommit: (index: number) => void;
}

export function useListboxNav({ open, count, onCommit }: ListboxNavOptions) {
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (i: number) => `${baseId}-option-${i}`;

  const [activeIndex, setActiveIndex] = useState(0);

  // A freshly opened or refiltered list highlights its first row.
  useEffect(() => {
    setActiveIndex(0);
  }, [open, count]);

  // Keep the highlighted option in view when the list is long enough to scroll.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(`${baseId}-option-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, baseId]);

  const active = open && count > 0;

  const onKeyDown = (e: KeyboardEvent) => {
    if (!active) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, count - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        onCommit(Math.min(activeIndex, count - 1));
        break;
    }
  };

  return {
    activeIndex,
    onKeyDown,
    listboxId,
    optionId,
    /** Spread onto the text input the list anchors to. */
    comboboxProps: {
      role: "combobox" as const,
      "aria-autocomplete": "list" as const,
      "aria-expanded": active,
      "aria-controls": active ? listboxId : undefined,
      "aria-activedescendant": active ? optionId(activeIndex) : undefined,
    },
  };
}
